import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Users, Activity, Settings, RefreshCw,
  FileText, ChevronRight, Check, Plus, RotateCcw, Code, Copy, Eye,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminService, configService, demoService } from '../services';
import { formatDate } from '../lib/utils';
import { AppLayout } from '../components/AppLayout';
import { toast } from 'sonner';
import { useDemo } from '../context/DemoContext';

import { EMPTY_STATES } from '../design-system/content';
import { getScoreBandClass } from '../design-system/tokens';
import { toTraceItemVM } from '../design-system/viewmodels';
import { PageHeader, StatusBadge, VerdictBadge, StatusIndicator, EmptyState } from '../components/shared';
import { FeatureFlagsPanel } from '../components/FeatureFlagsPanel';
import { CanonicalSyncPanel } from '../components/CanonicalSyncPanel';
import { CanonicalSyncBanner } from '../components/CanonicalSyncBanner';

const PROMPT_ROLES = [
  { id: 'extractor', label: 'Signal Extractor', desc: 'Extracts 13 measurable signals from AI-generated text' },
  { id: 'scorer', label: 'Scorer', desc: 'Computes dimension scores using formulas' },
  { id: 'optimizer', label: 'Optimizer', desc: 'Rewrites text for improved human impact' },
  { id: 'judge', label: 'Judge', desc: 'Verifies optimization preserves intent and safety' },
  { id: 'explanation_summarizer', label: 'Explanation Summarizer', desc: 'Summarizes HQ score results' },
  { id: 'json_repair', label: 'JSON Repair', desc: 'Fixes malformed JSON output' },
  { id: 'safety_fallback', label: 'Safety Fallback', desc: 'Evaluates text for harmful content' },
];

function PromptEditor({ role }) {
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSystem, setNewSystem] = useState('');
  const [newUser, setNewUser] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [autoActivate, setAutoActivate] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const [versionsData, activeData] = await Promise.all([
        adminService.getPromptVersions(role.id),
        adminService.getActivePrompt(role.id),
      ]);
      setVersions(versionsData.items || []);
      setActiveVersion(activeData);
      if (!selectedVersion && activeData) setSelectedVersion(activeData);
    } catch (err) {
      console.error('Failed to load prompt versions:', err);
    } finally {
      setLoading(false);
    }
  }, [role.id, selectedVersion]);

  useEffect(() => { loadVersions(); }, [role.id]);

  const handleActivate = async (version) => {
    try {
      await adminService.activatePromptVersion(role.id, version);
      toast.success(`Activated ${role.label} v${version}`);
      loadVersions();
    } catch { toast.error('Failed to activate version'); }
  };

  const handleRollback = async () => {
    try {
      const result = await adminService.rollbackPrompt(role.id);
      toast.success(`Rolled back ${role.label} to v${result.version}`);
      loadVersions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No previous version to roll back to');
    }
  };

  const handleCreate = async () => {
    if (!newSystem.trim() && !newUser.trim()) { toast.error('At least one template field is required'); return; }
    setSaving(true);
    try {
      const result = await adminService.createPromptVersion({
        role: role.id, system_template: newSystem, user_template: newUser, notes: newNotes, auto_activate: autoActivate,
      });
      toast.success(`Created ${role.label} v${result.version}`);
      setShowCreate(false); setNewSystem(''); setNewUser(''); setNewNotes(''); setAutoActivate(false);
      loadVersions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create version');
    } finally { setSaving(false); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };

  const prefillCreate = () => {
    if (activeVersion) { setNewSystem(activeVersion.system_template || ''); setNewUser(activeVersion.user_template || ''); }
    setShowCreate(true);
  };

  if (loading) return <div className="flex justify-center py-12"><span className="msml-spinner" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-white">{role.label}</h3>
          <p className="text-xs text-[var(--msml-text-muted)]">{role.desc}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRollback} className="msml-btn-secondary text-xs" data-testid={`rollback-${role.id}-btn`}>
            <RotateCcw className="w-3 h-3 mr-1" /> Rollback
          </Button>
          <Button size="sm" onClick={prefillCreate} className="msml-btn-primary text-xs" data-testid={`create-${role.id}-btn`}>
            <Plus className="w-3 h-3 mr-1" /> New Version
          </Button>
        </div>
      </div>

      {activeVersion && (
        <div className="msml-panel p-3 border-l-2 border-[var(--msml-success)]" data-testid={`active-prompt-${role.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <Check className="w-3 h-3 text-[var(--msml-success)]" />
            <StatusBadge label="Active" variant="success" />
            <span className="text-xs text-[var(--msml-text-secondary)] font-mono">v{activeVersion.version}</span>
            {activeVersion.source === 'code_default' && <StatusBadge label="Code Default" variant="warning" />}
          </div>
          <p className="text-xs text-[var(--msml-text-muted)] line-clamp-1">{activeVersion.notes || 'No notes'}</p>
        </div>
      )}

      {showCreate && (
        <div className="msml-card space-y-3" data-testid={`create-form-${role.id}`}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-white">New Version</h4>
            <button onClick={() => setShowCreate(false)} className="text-[var(--msml-text-muted)] hover:text-white text-xs">Cancel</button>
          </div>
          <div>
            <label className="text-xs text-[var(--msml-text-secondary)] mb-1 block">System Template</label>
            <textarea value={newSystem} onChange={(e) => setNewSystem(e.target.value)} className="w-full msml-input text-xs font-mono min-h-[120px] resize-y" placeholder="System prompt template..." data-testid={`system-template-input-${role.id}`} />
          </div>
          <div>
            <label className="text-xs text-[var(--msml-text-secondary)] mb-1 block">
              User Template <span className="text-[var(--msml-text-muted)] ml-1">(Variables: {'{text}'}, {'{scores}'}, {'{domain}'}, etc.)</span>
            </label>
            <textarea value={newUser} onChange={(e) => setNewUser(e.target.value)} className="w-full msml-input text-xs font-mono min-h-[80px] resize-y" placeholder="User prompt template with {variables}..." data-testid={`user-template-input-${role.id}`} />
          </div>
          <div>
            <label className="text-xs text-[var(--msml-text-secondary)] mb-1 block">Notes</label>
            <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} className="w-full msml-input text-xs" placeholder="What changed in this version..." data-testid={`notes-input-${role.id}`} />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-[var(--msml-text-secondary)] cursor-pointer">
              <input type="checkbox" checked={autoActivate} onChange={(e) => setAutoActivate(e.target.checked)} className="rounded border-white/20" data-testid={`auto-activate-${role.id}`} /> Auto-activate on save
            </label>
            <Button size="sm" onClick={handleCreate} disabled={saving} className="msml-btn-primary text-xs" data-testid={`save-version-${role.id}-btn`}>
              {saving ? 'Saving...' : 'Save Version'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h4 className="text-xs font-medium text-[var(--msml-text-secondary)] uppercase tracking-wider">Version History</h4>
        {versions.length === 0 ? (
          <p className="text-xs text-[var(--msml-text-muted)] py-4 text-center">No versions found — using code defaults</p>
        ) : (
          versions.map((v) => (
            <div key={`${v.role}-${v.version}`} className={`msml-panel p-3 cursor-pointer transition-colors ${selectedVersion?.version === v.version ? 'border-[var(--msml-primary)]/50 bg-[var(--msml-primary)]/5' : ''}`} onClick={() => setSelectedVersion(v)} data-testid={`prompt-version-${v.role}-${v.version}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-white">v{v.version}</span>
                  {v.is_active && <StatusBadge label="Active" variant="success" />}
                </div>
                {!v.is_active && (
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleActivate(v.version); }} className="text-[10px] h-6 px-2 text-[var(--msml-primary)] hover:text-[var(--msml-primary-hover)]" data-testid={`activate-${v.role}-${v.version}-btn`}>
                    Activate
                  </Button>
                )}
              </div>
              <p className="text-xs text-[var(--msml-text-muted)] line-clamp-1">{v.notes || 'No notes'}</p>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-[var(--msml-text-muted)]">
                <span>{v.created_by || 'system'}</span>
                <span>{v.created_at ? formatDate(v.created_at) : ''}</span>
                <span>{(v.variables || []).length} variables</span>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedVersion && selectedVersion.system_template && (
        <div className="msml-card space-y-3" data-testid={`version-detail-${role.id}`}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-white">v{selectedVersion.version} Details</h4>
            {selectedVersion.variables?.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {selectedVersion.variables.map((v) => (
                  <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--msml-primary-muted)] text-[var(--msml-primary)] font-mono">{'{' + v + '}'}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-[var(--msml-text-secondary)]">System Template</label>
              <button onClick={() => copyToClipboard(selectedVersion.system_template)} className="text-[var(--msml-text-muted)] hover:text-white"><Copy className="w-3 h-3" /></button>
            </div>
            <pre className="text-xs text-[var(--msml-text-secondary)] bg-[var(--msml-bg)] p-3 rounded-lg font-mono overflow-x-auto max-h-[200px] whitespace-pre-wrap">{selectedVersion.system_template}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-[var(--msml-text-secondary)]">User Template</label>
              <button onClick={() => copyToClipboard(selectedVersion.user_template)} className="text-[var(--msml-text-muted)] hover:text-white"><Copy className="w-3 h-3" /></button>
            </div>
            <pre className="text-xs text-[var(--msml-text-secondary)] bg-[var(--msml-bg)] p-3 rounded-lg font-mono overflow-x-auto max-h-[150px] whitespace-pre-wrap">{selectedVersion.user_template}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function DemoStatusCard() {
  const { demoMode, canToggle, toggle, refresh } = useDemo();
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await toggle();
      toast.success(`Demo mode ${!demoMode ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to toggle demo mode'); }
    finally { setToggling(false); }
  };

  return (
    <div className="msml-card" data-testid="demo-mode-config">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-[var(--msml-warning)]" />
          <h3 className="text-lg font-medium text-white">Demo Mode</h3>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded ${demoMode ? 'bg-[var(--msml-success-bg)] text-[var(--msml-success)]' : 'bg-white/5 text-[var(--msml-text-muted)]'}`}>
          {demoMode ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p className="text-sm text-[var(--msml-text-muted)] mb-4">
        {canToggle
          ? 'Demo mode enables pre-seeded sample runs for showcase. Toggle is available in dev/staging.'
          : 'Demo mode is controlled by the DEMO_MODE environment variable in production.'}
      </p>
      {canToggle && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleToggle}
          disabled={toggling}
          className="msml-btn-secondary text-xs"
          data-testid="demo-toggle-btn"
        >
          {toggling ? 'Toggling...' : demoMode ? 'Disable Demo' : 'Enable Demo'}
        </Button>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [trace, setTrace] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => { loadData(); }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const data = await adminService.getUsers();
        setUsers(data.items || []);
      } else if (activeTab === 'trace') {
        const data = await adminService.getTrace();
        setTrace((data.items || []).map(toTraceItemVM));
      } else if (activeTab === 'config') {
        const data = await adminService.getConfig();
        setConfig(data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminService.updateUserRole(userId, newRole);
      setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (err) { console.error('Failed to update role:', err); }
  };

  const handleModelConfigChange = async (role, provider, model) => {
    try { await adminService.updateModelConfig({ role, provider, model }); loadData(); }
    catch (err) { console.error('Failed to update config:', err); }
  };

  const handleReloadPrompts = async () => {
    try { await adminService.reloadPrompts(); toast.success('Prompt cache reloaded from DB'); }
    catch { toast.error('Failed to reload prompts'); }
  };

  return (
    <AppLayout activeNav="admin">
      <div className="p-4 lg:p-6">
        <div className="max-w-5xl mx-auto">
          <PageHeader
            icon={<Shield className="w-5 h-5 text-[var(--msml-primary)]" />}
            title="Admin Panel"
            subtitle="Manage users, prompts, trace logs, and configuration"
          />

          <CanonicalSyncBanner
            onClickSync={() => {
              setActiveTab('config');
              // Scroll to the canonical sync panel once it renders
              setTimeout(() => {
                document.querySelector('[data-testid="canonical-sync-panel"]')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 250);
            }}
          />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[var(--msml-panel)] border border-white/10 p-1">
              <TabsTrigger value="users" className="data-[state=active]:bg-[var(--msml-primary)] data-[state=active]:text-white" data-testid="admin-tab-users">
                <Users className="w-4 h-4 mr-2" /> Users
              </TabsTrigger>
              <TabsTrigger value="prompts" className="data-[state=active]:bg-[var(--msml-primary)] data-[state=active]:text-white" data-testid="admin-tab-prompts">
                <FileText className="w-4 h-4 mr-2" /> Prompts
              </TabsTrigger>
              <TabsTrigger value="trace" className="data-[state=active]:bg-[var(--msml-primary)] data-[state=active]:text-white" data-testid="admin-tab-trace">
                <Activity className="w-4 h-4 mr-2" /> Trace
              </TabsTrigger>
              <TabsTrigger value="config" className="data-[state=active]:bg-[var(--msml-primary)] data-[state=active]:text-white" data-testid="admin-tab-config">
                <Settings className="w-4 h-4 mr-2" /> Config
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-12"><span className="msml-spinner" /></div>
              ) : (
                <div className="msml-card overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-xs font-medium text-[var(--msml-text-muted)] p-4">User</th>
                        <th className="text-left text-xs font-medium text-[var(--msml-text-muted)] p-4">Email</th>
                        <th className="text-left text-xs font-medium text-[var(--msml-text-muted)] p-4">Role</th>
                        <th className="text-left text-xs font-medium text-[var(--msml-text-muted)] p-4">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.user_id} className="border-b border-white/5 last:border-0">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[var(--msml-primary-muted)] flex items-center justify-center text-[var(--msml-primary)] text-sm font-medium">
                                {user.name?.[0]?.toUpperCase() || 'U'}
                              </div>
                              <span className="text-sm text-white">{user.name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-[var(--msml-text-secondary)]">{user.email}</td>
                          <td className="p-4">
                            <Select value={user.role} onValueChange={(value) => handleRoleChange(user.user_id, value)}>
                              <SelectTrigger className="w-32 msml-input"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[var(--msml-panel)] border-white/10">
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-xs text-[var(--msml-text-muted)]">{formatDate(user.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* Prompts Tab */}
            <TabsContent value="prompts" className="space-y-4" data-testid="prompts-tab-content">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-base font-medium text-white">Dynamic Prompt Management</h3>
                  <p className="text-xs text-[var(--msml-text-muted)]">Edit LLM prompts without code changes. Create versions, activate, rollback.</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleReloadPrompts} className="msml-btn-secondary text-xs" data-testid="reload-prompts-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reload Cache
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4 space-y-1">
                  {PROMPT_ROLES.map((role) => (
                    <button key={role.id} onClick={() => setSelectedRole(role)} className={`w-full text-left p-3 rounded-lg transition-colors flex items-center justify-between group ${selectedRole?.id === role.id ? 'bg-[var(--msml-primary)]/10 border border-[var(--msml-primary)]/30' : 'bg-[var(--msml-panel)] border border-white/5 hover:border-white/10'}`} data-testid={`prompt-role-${role.id}`}>
                      <div>
                        <p className={`text-sm font-medium ${selectedRole?.id === role.id ? 'text-[var(--msml-primary)]' : 'text-white'}`}>{role.label}</p>
                        <p className="text-[10px] text-[var(--msml-text-muted)] line-clamp-1">{role.desc}</p>
                      </div>
                      <ChevronRight className={`w-4 h-4 transition-transform ${selectedRole?.id === role.id ? 'text-[var(--msml-primary)]' : 'text-[var(--msml-text-muted)] group-hover:text-[var(--msml-text-secondary)]'}`} />
                    </button>
                  ))}
                </div>
                <div className="lg:col-span-8">
                  {selectedRole ? (
                    <PromptEditor key={selectedRole.id} role={selectedRole} />
                  ) : (
                    <div className="msml-card flex flex-col items-center justify-center py-16">
                      <Code className="w-10 h-10 text-[var(--msml-text-muted)] mb-3" />
                      <p className="text-sm text-[var(--msml-text-muted)]">Select a prompt role to manage versions</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Trace Tab */}
            <TabsContent value="trace" className="space-y-4">
              <div className="flex justify-end mb-4">
                <Button variant="outline" size="sm" onClick={loadData} className="msml-btn-secondary" data-testid="refresh-trace-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><span className="msml-spinner" /></div>
              ) : trace.length === 0 ? (
                <EmptyState
                  icon={<Activity className="w-12 h-12" />}
                  title={EMPTY_STATES.trace.title}
                  body={EMPTY_STATES.trace.body}
                />
              ) : (
                <div className="space-y-3">
                  {trace.map((item) => (
                    <div key={item.runId} className="msml-panel p-4" data-testid={`trace-${item.runId}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-mono text-lg font-bold ${getScoreBandClass(item.hqScore)}`}>
                            {item.hqScore != null ? Number(item.hqScore).toFixed(1) : '--'}
                          </span>
                          <span className="text-xs text-[var(--msml-text-muted)] font-mono">{item.runId}</span>
                          <StatusIndicator status={item.status} />
                        </div>
                        <span className="text-xs text-[var(--msml-text-muted)] shrink-0">{formatDate(item.createdAt)}</span>
                      </div>

                      <p className="text-sm text-[var(--msml-text-secondary)] mb-2 line-clamp-1">{item.originalText}</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[10px] mb-2">
                        <div><span className="text-[var(--msml-text-muted)]">Rubric: </span><span className="text-[var(--msml-text-secondary)]">{item.rubricVersion || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Prompt: </span><span className="text-[var(--msml-text-secondary)]">{item.promptVersion || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Scorer: </span><span className="text-[var(--msml-text-secondary)] font-mono">{item.scorerModel || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Mode: </span><span className="text-[var(--msml-text-secondary)] capitalize">{item.processingMode || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Optimizer: </span><span className="text-[var(--msml-text-secondary)] font-mono">{item.optimizerModel || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Judge: </span><span className="text-[var(--msml-text-secondary)] font-mono">{item.judgeModel || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">Domain: </span><span className="text-[var(--msml-text-secondary)]">{item.domain || '—'}</span></div>
                        <div><span className="text-[var(--msml-text-muted)]">User: </span><span className="text-[var(--msml-text-secondary)] font-mono">{item.userId?.substring(0, 12)}</span></div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap text-[10px]">
                        {item.determinismHash && <span className="text-[var(--msml-text-muted)] font-mono">Hash: {item.determinismHash.substring(0, 16)}...</span>}
                        {item.hasOptimization && <span className="text-[var(--msml-success)]">Optimized</span>}
                        {item.hasJudge && <VerdictBadge verdict={item.judgeVerdict} />}
                        {item.capsApplied?.length > 0 && <StatusBadge label={`${item.capsApplied.length} cap(s)`} variant="warning" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Config Tab */}
            <TabsContent value="config" className="space-y-6">
              {loading ? (
                <div className="flex justify-center py-12"><span className="msml-spinner" /></div>
              ) : (
                <>
                  <div className="msml-card" data-testid="model-config">
                    <h3 className="text-lg font-medium text-white mb-4">Model Configuration</h3>
                    <p className="text-sm text-[var(--msml-text-muted)] mb-4">Configure which models are used for each role. Changes take effect immediately.</p>
                    <div className="space-y-4">
                      {config?.model_config && Object.entries(config.model_config).map(([role, roleConfig]) => (
                        <div key={role} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 border-b border-white/10 last:border-0">
                          <div>
                            <p className="text-sm text-white capitalize">{role}</p>
                            <p className="text-xs text-[var(--msml-text-muted)]">{roleConfig.description || `Used for ${role}`}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select value={roleConfig.provider} onValueChange={(provider) => { const models = config.available_models[provider]; if (models?.length) handleModelConfigChange(role, provider, models[0]); }}>
                              <SelectTrigger className="w-32 msml-input"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[var(--msml-panel)] border-white/10">
                                {Object.keys(config.available_models || {}).map((provider) => (<SelectItem key={provider} value={provider} className="capitalize">{provider}</SelectItem>))}
                              </SelectContent>
                            </Select>
                            <Select value={roleConfig.model} onValueChange={(model) => handleModelConfigChange(role, roleConfig.provider, model)}>
                              <SelectTrigger className="w-48 msml-input"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[var(--msml-panel)] border-white/10">
                                {(config.available_models?.[roleConfig.provider] || []).map((model) => (<SelectItem key={model} value={model}>{model}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <DemoStatusCard />

                  <CanonicalSyncPanel />

                  <FeatureFlagsPanel />

                  <div className="msml-card" data-testid="scoring-rubric">
                    <h3 className="text-lg font-medium text-white mb-4">Scoring Rubric</h3>
                    <p className="text-sm text-[var(--msml-text-muted)] mb-4">Current scoring dimensions and their weights.</p>
                    <div className="space-y-3">
                      {config?.scoring_rubric?.dimensions && Object.entries(config.scoring_rubric.dimensions).map(([name, dim]) => (
                        <div key={name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div>
                            <p className="text-sm text-white capitalize">{name.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-[var(--msml-text-muted)]">{dim.description}</p>
                          </div>
                          <span className="text-sm font-mono text-[var(--msml-primary)]">{Math.round(dim.weight * 100)}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-[var(--msml-text-muted)]">Rubric Version: {config?.scoring_rubric?.version || '1.0.0'}</p>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppLayout>
  );
}
